import { useState, useEffect, useCallback } from "react";
import { FaSearch, FaUserPlus, FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { showUsers, updateUser } from "../http/api";
import { useAuth } from "../http/authcontex";
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import Loading from "../loading/loading";
import "../common/style.css";
import "./user.css"; // reuse project styles for uniform look

export default function ShowUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem("sb|sidebar-toggle") === "true"
  );

  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    localStorage.setItem("sb|sidebar-toggle", isSidebarOpen);
  }, [isSidebarOpen]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    localStorage.setItem("sb|sidebar-toggle", !isSidebarOpen);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await showUsers();
      if (response.status === 200) {
        setUsers(response.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      if (error.response?.status === 401) {
        auth.logout();
        navigate("/");
      } else {
        console.error("Error fetching users:", error);
      }
    }
  }, [auth, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const filtered = users.filter((user) =>
      Object.values(user).some((val) =>
        typeof val === "string" &&
        val.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleAdd = () => {
    navigate("/users/add");
  };

  const handleEdit = (user) => {
    navigate(`/users/edit/${user.id}`);
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await updateUser({ id, active: !currentStatus });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, active: !currentStatus } : u
        )
      );
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile,
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <button
          className={`btn btn-sm ${
            row.active ? "btn-success" : "btn-secondary"
          }`}
          onClick={() => toggleActive(row.id, row.active)}
        >
          {row.active ? "Active" : "Inactive"}
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleEdit(row)}
        >
          <FaEdit /> Edit
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="ShowUsers">
      {loading && <Loading />}
      <div
        className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}
      >
        <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div id="layoutSidenav">
          <SideNave isSidebarOpen={isSidebarOpen} />
          <div id="layoutSidenav_content">
            <main>
              <div className="container-fluid px-4">
                <h4 className="mt-4">User Management</h4>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <button
                        className="btn btn-sm btn-success me-2"
                        onClick={handleAdd}
                      >
                        <FaUserPlus /> Add User
                      </button>
                    </div>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        placeholder="Search..."
                        className="form-control me-2"
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                      <button className="btn btn-sm btn-secondary">
                        <FaSearch />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card mb-4">
                  <div className="card-header">
                    <i className="fas fa-users me-1"></i>
                    User Details
                  </div>
                  <div className="card-body shadow-sm border-0">
                    <DataTable
                      className="data-table"
                      columns={columns}
                      data={filteredUsers}
                      pagination
                      highlightOnHover
                    />
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
